import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { uniq } from 'lodash';

import { resourceIconMap } from '@dbt-labs/dbt-dag';
import {
  FileTreeItemType,
  RyeconFile,
  RyeconFolder,
  useOpenDirectories,
} from '@dbt-labs/sourdough';

import {
  EnvironmentDeploymentType,
  SearchFieldType,
  SearchPlatform,
} from '@/__generated__/graphql';
import { useFavoriteAssets } from '@/hooks/admin/favoriteAssets';
import {
  useFavoriteProjects,
  useFavoriteProjectsData,
} from '@/hooks/admin/favoriteProjects';
import { Project, useProjectLazy } from '@/hooks/admin/project';
import { useLazyAccountSearchResults } from '@/hooks/discovery/accountSearch';
import { useLazyWarehouseEntities } from '@/hooks/discovery/catalog/entities';
import { useLinkToExplorer } from '@/hooks/links';
import { UrlEnvironmentType } from '@/typings/url';
import { truthy } from '@/util/array';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import ActionRenderer from './ActionRenderer';
import { isValidFileType, VALID_FILE_TYPES, ValidFileType } from './const';
import { RyeconFavoriteFilled } from './RyeconFavorite';
import { TreeData } from './types';
import {
  generateFileWithLogo,
  generateFolderWithIcon,
  useMenuActions,
  WarehouseAssetTypeIconMapping,
} from './utils';

const getAssetIcon = (assetType?: string, name?: string) => {
  const icon =
    resourceIconMap[assetType as keyof typeof resourceIconMap] ??
    WarehouseAssetTypeIconMapping[
      assetType as keyof typeof WarehouseAssetTypeIconMapping
    ];

  if (!assetType || !icon) {
    return { ryecon: RyeconFile, label: name ?? 'File' };
  }
  return {
    ryecon: icon,
    label: name ?? 'Asset',
  };
};

const ROOT_NODE_NAME = 'favorites';

const rootNode = generateFolderWithIcon(ROOT_NODE_NAME, {
  iconOverride: {
    ryecon: RyeconFavoriteFilled,
    label: 'Favorites',
  },
  name: 'Favorites',
  data: {
    pagination: {
      page: 0,
      hasMore: false,
      total: 0,
      isLoading: false,
    },
  },
});

// Connection —> Database —> Project —> Schema —> Other assets
const typeOrder = ['connection', 'database', 'project', 'schema'];

const sortByType = (a: string, b: string, favoriteProjectsData: Project[]) => {
  // get all parts of the path and clean up the empty parts
  const aParts = a.split('/').filter((part) => part !== '');
  const bParts = b.split('/').filter((part) => part !== '');

  // if the parts of either side is 1, it means the root, we skip this
  if (aParts.length !== 2 || bParts.length !== 2) {
    return 0;
  }

  // get the type and name of the asset
  const aType = aParts[1].substring(1).split(':')[0].toLowerCase();
  const bType = bParts[1].substring(1).split(':')[0].toLowerCase();
  const aName = aParts[1].substring(1).split(':')[1].toLowerCase();
  const bName = bParts[1].substring(1).split(':')[1].toLowerCase();

  // get the index of the type in the type order
  const aIndex = typeOrder.indexOf(aType);
  const bIndex = typeOrder.indexOf(bType);
  if (aIndex === -1) {
    return 1;
  }
  if (bIndex === -1) {
    return -1;
  }

  // if the types exists in the order and are the same, we sort by name
  if (aType === bType) {
    if (aType === 'project') {
      const aProject = favoriteProjectsData?.find(
        (project) => project.id === Number(aName),
      );
      const bProject = favoriteProjectsData?.find(
        (project) => project.id === Number(bName),
      );
      if (aProject && bProject) {
        return aProject.name.localeCompare(bProject.name);
      }
    }
    return aName.localeCompare(bName);
  }

  // if the types exist in the order and are different, we sort by the index of the type
  return aIndex - bIndex;
};

export const useFavoriteTree = (): TreeData => {
  const { accountId } = useDbtCloudAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const resourceDetails = useLinkToExplorer('resourceDetails');
  const projectDetails = useLinkToExplorer('project');
  const catalogAssetDetails = useLinkToExplorer('catalogAsset');
  const [verifiedAssets, setVerifiedAssets] = useState<string[]>([]);
  const [isVerifiedAssetsInitialized, setIsVerifiedAssetsInitialized] = useState(false);
  const {
    data: favoriteAssets,
    isLoading: isLoadingFavoriteAssets,
    isFetching: isFetchingFavoriteAssets,
  } = useFavoriteAssets(accountId);
  const {
    data: favoriteProjects,
    isLoading: isLoadingFavoriteProjects,
    isFetching: isFetchingFavoriteProjects,
  } = useFavoriteProjects(accountId);
  const {
    data: favoriteProjectsData,
    isLoading: isLoadingFavoriteProjectsData,
    isFetching: isFetchingFavoriteProjectsData,
    refetch: refetchFavoriteProjectsData,
  } = useFavoriteProjectsData(
    accountId,
    favoriteProjects?.map((project) => project.project_id!),
  );

  const [, getProject] = useProjectLazy();
  const [, getWarehouseEntities] = useLazyWarehouseEntities();
  const [, getAccountSearchResults] = useLazyAccountSearchResults();
  const searchQueryUniqueIds = useMemo(
    () =>
      favoriteAssets
        ?.filter((asset) =>
          [SearchPlatform.Dbt].includes(
            asset.platform?.toLocaleLowerCase() as SearchPlatform,
          ),
        )
        ?.map((asset) => asset.asset_id) ?? [],
    [favoriteAssets],
  );

  const searchQueryUrns = useMemo(
    () =>
      favoriteAssets
        ?.filter((asset) =>
          [SearchPlatform.Snowflake].includes(
            asset.platform?.toLocaleLowerCase() as SearchPlatform,
          ),
        )
        ?.map((asset) => asset.asset_id) ?? [],
    [favoriteAssets],
  );

  const validEnvironmentIds = useMemo(
    () =>
      uniq(
        favoriteAssets?.map((asset) => Number(asset.environment_id))?.filter(Boolean),
      ),
    [favoriteAssets],
  );

  const validateAssets = useCallback(async () => {
    const { data: warehouseEntities } = await getWarehouseEntities({
      accountId,
      // favorite support at most 300 assets
      first: 300,
      filter: {
        urns: searchQueryUrns,
      },
    });
    const validAccountSearchResults = await getAccountSearchResults({
      accountId,
      filter: {
        assetTypes: [SearchPlatform.Dbt],
        urns: null,
        fields: [SearchFieldType.Name],
        query: '',
        resourceTypes: null,
        tags: [],
        materializationTypes: null,
        modelingLayers: null,
        accessLevels: null,
        dbtEnvs: [
          EnvironmentDeploymentType.Production,
          EnvironmentDeploymentType.Staging,
        ],
        environmentIds: validEnvironmentIds,
        projectIds: null,
        uniqueIds: searchQueryUniqueIds,
        warehouseEntityTypes: null,
      },
    });

    const validAssets = [
      ...warehouseEntities.map((asset) => asset.urn),
      ...validAccountSearchResults.data
        .map((result) => result.hit.dbtAsset?.uniqueId)
        .filter(truthy),
    ];

    setVerifiedAssets(validAssets);
    setIsVerifiedAssetsInitialized(true);
  }, [
    searchQueryUrns,
    getAccountSearchResults,
    accountId,
    validEnvironmentIds,
    searchQueryUniqueIds,
    getWarehouseEntities,
  ]);

  useEffect(() => {
    if (favoriteAssets) {
      validateAssets();
    }
    // We only need to run this when the favorite assets change
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteAssets]);

  // We need to refetch the favorite projects data when the favorite projects change
  useEffect(() => {
    if (favoriteProjects) {
      refetchFavoriteProjectsData();
    }
  }, [favoriteProjects, refetchFavoriteProjectsData]);

  const { getMenuActions } = useMenuActions();

  const favoriteTree = useMemo(() => {
    const assets: FileTreeItemType[] = (favoriteAssets || [])?.map((asset) => {
      const isValidAsset = verifiedAssets.includes(asset.asset_id);
      const actions = getMenuActions({
        type: 'asset',
        projectId: asset.project_id,
        assetId: asset.asset_id,
        environmentId: asset.environment_id,
        assetType: asset.asset_type,
        platform: asset.platform,
      });
      return generateFileWithLogo(
        `${ROOT_NODE_NAME}/${asset.asset_type}:${asset.asset_id.toString()}`,
        {
          name: asset.name,
          iconOverride: getAssetIcon(asset.asset_type, asset.name),
          data: {
            disabled: !isValidAsset,
            tooltipText: isValidAsset ? asset.name : `${asset.name} (Not available)`,
            rawData: {
              assetType: 'asset',
              name: asset.name,
              platform: asset.platform,
            },
            customActionsRenderer:
              VALID_FILE_TYPES.ASSETS.includes(asset.asset_type ?? '') &&
              isValidAsset ? (
                <ActionRenderer
                  projectId={asset.project_id ?? 0}
                  assetId={asset.asset_id}
                  environmentId={Number(asset.environment_id)}
                  assetType={asset.asset_type ?? ''}
                  initialActions={actions?.[0]}
                />
              ) : undefined,
            actions,
          },
        },
      );
    });

    const filteredProjects =
      favoriteProjectsData?.filter((project) =>
        favoriteProjects?.some((favorite) => favorite.project_id === project.id),
      ) ?? [];

    const projects: FileTreeItemType[] = filteredProjects.map((project) =>
      generateFileWithLogo(`${ROOT_NODE_NAME}/project:${project?.id?.toString()}`, {
        name: project?.name?.toString() ?? '',
        data: {
          tooltipText: project?.name?.toString() ?? '',
          rawData: {
            assetType: 'project',
            name: project.name,
            platform: 'dbt',
          },
          actions: getMenuActions({
            type: 'project',
            projectId: project.id!,
          }),
        },
        iconOverride: {
          ryecon: RyeconFolder,
          label: project?.name?.toString() ?? '',
        },
      }),
    );

    return [rootNode, ...assets, ...projects];
  }, [
    favoriteAssets,
    favoriteProjectsData,
    getMenuActions,
    verifiedAssets,
    favoriteProjects,
  ]);

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    items: favoriteTree,
    activeFilePath: undefined,
    rootNodeName: ROOT_NODE_NAME,
  });

  const onFileSelect = useCallback(
    async (file?: string) => {
      const filePath = file?.split('/').pop() ?? '';
      const fileType = filePath.split(':')[0].toLowerCase() as ValidFileType;
      const fileId = filePath.split(':')[1];

      if (!isValidFileType(fileType)) {
        return;
      }

      if (VALID_FILE_TYPES.ASSETS.includes(fileType)) {
        const favoriteAsset = favoriteAssets?.find(
          (asset) => asset.asset_id === fileId,
        );
        const projectData = await getProject({
          accountId: accountId ?? 0,
          projectId: favoriteAsset?.project_id ?? 0,
        });

        if (!projectData.data) {
          return;
        }

        const environmentType = projectData.data.environments?.find(
          (environment) => environment.id === favoriteAsset?.environment_id,
        )?.type;

        navigate(
          resourceDetails({
            accountId: accountId ?? 0,
            projectId: favoriteAsset?.project_id ?? 0,
            environmentType: (environmentType ?? 'production') as UrlEnvironmentType,
            dbtUniqueId: fileId,
          }).concat(location.search),
        );
      } else if (VALID_FILE_TYPES.PROJECTS.includes(fileType)) {
        const favoriteProjectData = favoriteProjectsData?.find(
          (project) => project.id === Number(fileId),
        );

        if (!favoriteProjectData) {
          return;
        }

        navigate(
          projectDetails({
            accountId: accountId ?? 0,
            projectId: favoriteProjectData?.id ?? 0,
            environmentType: 'production' as UrlEnvironmentType,
          }).concat(location.search),
        );
      } else if (VALID_FILE_TYPES.WAREHOUSE_ENTITIES.includes(fileType)) {
        navigate(
          catalogAssetDetails({
            accountId: accountId ?? 0,
            assetId: fileId,
          }).concat(location.search),
        );
      } else {
        return;
      }
    },
    [
      accountId,
      catalogAssetDetails,
      favoriteAssets,
      favoriteProjectsData,
      getProject,
      location.search,
      navigate,
      projectDetails,
      resourceDetails,
    ],
  );

  useEffect(() => {
    setOpenDirectories([ROOT_NODE_NAME]);
    // We only need to run this once
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    fileTree: favoriteTree,
    generateChildrenForPath: () => {},
    selectedFile: undefined,
    setSelectedFile: onFileSelect,
    selectedFolder: undefined,
    setSelectedFolder: () => {},
    openDirectories,
    setOpenDirectories,
    onSort: (a, b) => sortByType(a, b, favoriteProjectsData ?? []),
    isLoading:
      isFetchingFavoriteAssets ||
      isFetchingFavoriteProjects ||
      isFetchingFavoriteProjectsData,
    initialized:
      !isLoadingFavoriteAssets &&
      !isLoadingFavoriteProjects &&
      !isLoadingFavoriteProjectsData &&
      isVerifiedAssetsInitialized,
  };
};
